import React from 'react';
import styled from 'styled-components';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FiChevronDown, FiChevronRight, FiEdit2, FiTrash2, FiPlus, FiRotateCcw } from 'react-icons/fi';

const CategoryContainer = styled.div`
  margin: 0;
  padding: 0;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 8px 12px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => props.$clickable ? 'rgba(255, 255, 255, 0.03)' : 'transparent'};
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #888;
  font-size: 14px;
  transition: transform 0.2s ease;
`;

const CategoryTitle = styled.div`
  flex: 1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$isHidden ? '#666' : '#888'};
  opacity: ${props => props.$isHidden ? 0.6 : 1};
`;

const CategoryActions = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  gap: 4px;
`;

const CategoryActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #888;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    border-color: #555;
  }

  &.delete:hover {
    background: rgba(244, 67, 54, 0.1);
    color: #f44336;
    border-color: #f44336;
  }

  &.reset {
    opacity: 0.5;
    
    &:hover {
      opacity: 1;
      background: rgba(76, 175, 80, 0.1);
      color: #4CAF50;
      border-color: #4CAF50;
    }
  }

  svg {
    font-size: 12px;
  }
`;

const ItemsContainer = styled.div`
  display: ${props => props.$collapsed ? 'none' : 'block'};
  background: ${props => props.$isOver ? 'rgba(76, 175, 80, 0.05)' : 'transparent'};
  border-left: ${props => props.$isOver ? '2px solid #4CAF50' : '2px solid transparent'};
  margin: 0;
  padding: 0;
  transition: all 0.2s ease;
  min-height: ${props => props.$isEmpty ? '40px' : 'auto'};
`;

const EmptyMessage = styled.div`
  padding: 12px 15px;
  color: #666;
  font-size: 12px;
  font-style: italic;
  text-align: center;
`;

const ItemCount = styled.span`
  color: #666;
  font-size: 10px;
  font-weight: 400;
  margin-left: 4px;
`;

export default function CategorySection({
  category,
  items,
  children,
  editMode,
  menuMinimized,
  onToggleCollapse,
  onRename,
  onDelete,
  onAddItem,
  onReset
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: category.id,
    disabled: !editMode
  });

  const itemIds = items.map(item => item.id);
  const isEmpty = items.length === 0;
  const isHidden = category.id === 'hidden';

  const handleHeaderClick = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  return (
    <CategoryContainer>
      {!menuMinimized && (
        <CategoryHeader 
          onClick={handleHeaderClick}
          $clickable={!!onToggleCollapse}
        >
          {onToggleCollapse && (
            <CollapseIcon>
              {category.collapsed ? <FiChevronRight /> : <FiChevronDown />}
            </CollapseIcon>
          )}
          
          <CategoryTitle $isHidden={isHidden}>
            {category.name}
            {items.length > 0 && <ItemCount>({items.length})</ItemCount>}
          </CategoryTitle>

          <CategoryActions $visible={editMode && !category.isSystem}>
            {onRename && (
              <CategoryActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  onRename();
                }}
                title="Renomear categoria"
              >
                <FiEdit2 />
              </CategoryActionButton>
            )}
            
            {onDelete && (
              <CategoryActionButton
                className="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                title="Deletar categoria"
              >
                <FiTrash2 />
              </CategoryActionButton>
            )}
          </CategoryActions>

          {/* Botão de reset para categoria dashboards */}
          {category.id === 'dashboards' && onReset && (
            <CategoryActions $visible={true}>
              <CategoryActionButton
                className="reset"
                onClick={(e) => {
                  e.stopPropagation();
                  onReset();
                }}
                title="Restaurar todos os itens ao padrão"
              >
                <FiRotateCcw />
              </CategoryActionButton>
            </CategoryActions>
          )}

          {editMode && onAddItem && (
            <CategoryActions $visible={true}>
              <CategoryActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  onAddItem();
                }}
                title="Adicionar item"
              >
                <FiPlus />
              </CategoryActionButton>
            </CategoryActions>
          )}
        </CategoryHeader>
      )}

      <ItemsContainer
        ref={setNodeRef}
        $collapsed={category.collapsed}
        $isOver={isOver}
        $isEmpty={isEmpty}
      >
        {isEmpty && editMode && !category.collapsed ? (
          <EmptyMessage>
            Arraste itens para cá
          </EmptyMessage>
        ) : (
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {children}
          </SortableContext>
        )}
      </ItemsContainer>
    </CategoryContainer>
  );
}
